import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Link } from 'react-router-dom';
import { getBorderColor } from '../utils/notificationUtils';
import { NotificationMetadata } from './NotificationMetadata';
import { NotificationActions } from './NotificationActions';
import { ResponseDialog } from './ResponseDialog';
import type { Notification } from '../types/notification.types';

interface NotificationCardProps {
  notification: Notification;
  userRole: string;
  isUpdating: boolean;
  openDialogId: string | null;
  observation: string;
  onButtonClick: (notification: Notification) => void;
  onResolveClick: (notification: Notification) => void;
  onObservationChange: (value: string) => void;
  onCloseDialog: () => void;
  onSubmitResponse: (notification: Notification) => void;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  userRole,
  isUpdating,
  openDialogId,
  observation,
  onButtonClick,
  onResolveClick,
  onObservationChange,
  onCloseDialog,
  onSubmitResponse,
}) => {
  const borderColor = getBorderColor(notification.notificationStatus?.id);
  const isDialogOpen = openDialogId === notification.id;

  return (
    <>
      <div className={`flex bg-card rounded-lg shadow-md ${borderColor} border-l-4`}>
        <div className="flex-1 p-6">
          <div className="flex items-center gap-2 mb-1">
            {notification.accommodationId ? (
              <Link
                to={`/accommodation/${notification.accommodationId}`}
                className="text-lg font-bold text-primary hover:underline"
              >
                {notification.accommodation?.establishmentName ?? "Sin nombre"}
              </Link>
            ) : (
              <span className="text-lg font-bold text-primary">
                {notification.accommodation?.establishmentName ?? "Sin nombre"}
              </span>
            )}
            <Badge
              variant={
                notification.notificationStatus?.name?.toLowerCase() === "abierta"
                  ? "destructive"
                  : notification.notificationStatus?.name?.toLowerCase() === "resuelta"
                  ? "success"
                  : "secondary"
              }
              className="ml-2"
            >
              {notification.notificationStatus?.name}
            </Badge>
          </div>
          
          <NotificationMetadata notification={notification} />
          
          <div className="mb-2 text-base text-foreground">
            {notification.description}
          </div>
          
          {notification.response && notification.response.trim() && (
            <div className="text-sm text-muted-foreground border-l-2 border-muted pl-3 mt-2">
              <span className="font-semibold">Respuesta:</span>{" "}
              {notification.response}
              {notification.respondedByUser && (
                <div className="mt-1">
                  <span className="font-semibold">Respondido por:</span>{" "}
                  {notification.respondedByUser.firstName}{" "}
                  {notification.respondedByUser.lastName}
                </div>
              )}
            </div>
          )}
        </div>
        
        <NotificationActions
          notification={notification}
          userRole={userRole}
          isUpdating={isUpdating}
          onButtonClick={onButtonClick}
          onResolveClick={onResolveClick}
        />
      </div>
      
      <ResponseDialog
        notification={notification}
        isOpen={isDialogOpen}
        observation={observation}
        isUpdating={isUpdating}
        onObservationChange={onObservationChange}
        onClose={onCloseDialog}
        onSubmit={onSubmitResponse}
      />
    </>
  );
}; 